import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  CircularProgress, Chip,
  Typography,
  InputAdornment,
  Box,
  AppBar,
  Toolbar,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import LoadingBar from 'react-top-loading-bar';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import MuiAlert from '@mui/material/Alert';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AllTopics = () => {
  const navigate = useNavigate();

  // State for dropdowns
  const [teacherId, setTeacherId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [subjectId, setSubjectId] = useState('');

  // State for dropdown data
  const [teacherData, setTeacherData] = useState([]);
  const [classData, setClassData] = useState([]);
  const [sectionData, setSectionData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);

  // State for topics and UI
  const [topics, setTopics] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showModal, setShowModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState({ topicId: null, topicName: '' });
  const [canDelete, setCanDelete] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [canAdd, setCanAdd] = useState(true);
  const [isMaster, setIsMaster] = useState(false);
const [branches, setBranches] = useState([]);
const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Fetch Branches Data
    const fetchBranches = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });

    if (response.ok) {
      const data = await response.json();
      setBranches(data);
    } else {
      console.error("Failed to fetch branches");
    }
  } catch (error) {
    console.error("Error fetching branches:", error);
  }
};

useEffect(() => {
  const isMasterUser = sessionStorage.getItem("is_Master") === "true";
  setIsMaster(isMasterUser);

  if (!isMasterUser) {
    const branchId = sessionStorage.getItem("branchId");
    setSelectedBranchId(branchId);
    return;
  }

  // For master users
  fetchBranches().then(() => {
    // After branches are loaded → auto-select first one if nothing is selected
    setBranches((prevBranches) => {
      if (prevBranches.length > 0 && !selectedBranchId) {
        const defaultBranch = prevBranches[0];
        setSelectedBranchId(defaultBranch.branchId);
        // Optional: you can show a small info once
        // setSnackbarMessage(`Auto-selected branch: ${defaultBranch.branchName}`);
        // setOpenSnackbar(true);
        // setSnackbarSeverity('info');
      }
      return prevBranches;
    });
  });
}, []);

// Auto-select first branch when branches are loaded (for master users only)
useEffect(() => {
  if (!isMaster || branches.length === 0 || selectedBranchId) {
    return;
  }

  // Select the first branch by default
  const defaultBranch = branches[0];
  setSelectedBranchId(defaultBranch.branchId);

  // Optional: feedback
  // setSnackbarMessage(`Default branch selected: ${defaultBranch.branchName}`);
  // setOpenSnackbar(true);
  // setSnackbarSeverity('info');

}, [branches, isMaster, selectedBranchId]);

  // Fetch Teacher Data
  const fetchTeacherData = async () => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');
      if (!branchId) {
      setTeacherData([]);
      setLoadingBarProgress(0);
      return;
    }
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/GetTeacher?Branch_Id=${branchId}`,
         {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        setTeacherData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch teacher data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setTeacherData([]);
    }
  };

  // Fetch Class Data
  const fetchClassData = async (selectedTeacherId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');
        if (!branchId) {
      setClassData([]);
      setLoadingBarProgress(100);
      setSnackbarMessage("No branch assigned. Contact admin.");
      setOpenSnackbar(true);
      setSnackbarSeverity('warning');
      return;
    }
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/GetClass?Branch_Id=${branchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ teacherId: selectedTeacherId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setClassData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch class data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setClassData([])
    }
  };

  // Fetch Section Data
  const fetchSectionData = async (selectedClassId, selectedTeacherId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/ddlSection_clsId`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classId: selectedClassId, teacherId: selectedTeacherId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setSectionData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch section data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setSectionData([])
    }
  };

  // Fetch Subject Data
  const fetchSubjectData = async (selectedClassId, selectedTeacherId, selectedSectionId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');

     if (!branchId) {
      setSubjectData([]);
      setLoadingBarProgress(100);
      setSnackbarMessage("No branch assigned. Contact admin.");
      setOpenSnackbar(true);
      setSnackbarSeverity('warning');
      return;
    }

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/Subjectddl?Branch_Id=${branchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classId: selectedClassId, teacherId: selectedTeacherId, sectionId: selectedSectionId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setSubjectData(responseData); setSubjectData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch subject data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setSubjectData([])
    }
  };

  // Fetch Topics Data
  const fetchTopicsData = async (selectedClassId, selectedSectionId, selectedSubjectId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');

     if (!branchId) {
      console.warn(`fetchTopicsData aborted — branchId is falsy`, { isMaster, selectedBranchId, stored: sessionStorage.getItem('branchId') });
      setTopics([]);
      setFilteredData([]);
      setLoadingBarProgress(100);

      if (isMaster) {
        setSnackbarMessage("Please select a branch first");
      } else {
        setSnackbarMessage("No branch assigned. Contact admin.");
      }
      setOpenSnackbar(true);
      setSnackbarSeverity('warning'); 
      return;
    }

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/TeachingTopics/GetByClassSubject?Branch_Id=${branchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classId: selectedClassId, sectionId: selectedSectionId, subjectId: selectedSubjectId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        // Extract the topics array from the response object
        setTopics(responseData.topics || []);
        setFilteredData(responseData.topics || []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch topics');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };


  // Fetch Teacher Data on Component Mount
  useEffect(() => {
  if (selectedBranchId || !isMaster) {
    fetchTeacherData();
    // Reset dependent selections when branch changes
    setTeacherId('');
    setClassId('');
    setSectionId('');
    setSubjectId('');
    setTopics([]);
    setFilteredData([]);
  }
}, [selectedBranchId, isMaster]);

  // Fetch Class Data when Teacher is Selected
  useEffect(() => {
    if (teacherId) {
      fetchClassData(teacherId);
      setClassId('');
      setSectionId('');
      setSubjectId('');
    }
  }, [teacherId]);

  // Fetch Section Data when Class is Selected
  useEffect(() => {
    if (classId && teacherId) {
      fetchSectionData(classId, teacherId);
      setSectionId('');
      setSubjectId('');
    }
  }, [classId, teacherId]);

  // Fetch Subject Data when Section is Selected
  useEffect(() => {
    if (sectionId && classId && teacherId) {
      fetchSubjectData(classId, teacherId, sectionId);
      setSubjectId('');
    }
  }, [sectionId, classId, teacherId]);

  // Fetch Topics when Subject is Selected
  useEffect(() => {
    if (subjectId && sectionId && classId) {
      fetchTopicsData(classId, sectionId, subjectId);
    }
  }, [subjectId, sectionId, classId]);

  // Add this useEffect after the existing useEffects
  useEffect(() => {
    if (searchTerm) {
      setFilteredData(
        topics.filter((topic) =>
          topic.topicName.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredData(topics);
    }
    setPage(1); // Reset to first page when searching
  }, [searchTerm, topics]);

  // Function to check if delete button should be disabled
  const shouldDisableDelete = (topicStatus) => {
    return topicStatus === 'C' || topicStatus === 'Completed';
  };

  // Pagination Logic
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Modal Logic
  const handleShow = (topicId, topicName, topicStatus) => {
    // Check if topic is completed before showing delete modal
    if (shouldDisableDelete(topicStatus)) {
      setSnackbarMessage('Cannot delete completed topics');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      return;
    }

    setSelectedTopic({ topicId, topicName });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleDelete = async () => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/TeachingTopics/Delete/${selectedTopic.topicId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
        }
      );

      if (response.ok) {
        setFilteredData(filteredData.filter((topic) => topic.topicId !== selectedTopic.topicId));
        setShowModal(false);
        setLoadingBarProgress(100);
        setSnackbarMessage('Topic deleted successfully!');
        setOpenSnackbar(true);
        setSnackbarSeverity('success');
      } else {
        const errorData = await response.json();
        setSnackbarMessage(errorData.message || 'Failed to delete topic');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setLoadingBarProgress(0);
    }
  };


  const handleEdit = (topicId) => {
    navigate(`/edit-topic/${topicId}?Branch_Id=${isMaster ? selectedBranchId : sessionStorage.getItem('branchId')}`);
  };

  const handleAdd = () => {
    navigate('/add-all-topic');
  };

  // Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <AppBar
  position="static"
  elevation={0}
  sx={{
    backgroundColor: '#0B1F3D',
    borderRadius: '8px 8px 0 0',
    mb: 0,
  }}
>
  <Toolbar
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.5,
      px: 3,
    }}
  >
    {/* Left: Title */}
    <Typography
      variant="h4"
      component="div"
      sx={{
        fontWeight: 600,
        letterSpacing: '0.5px',
        color: '#ffffff',
      }}
    >
      All Topics
    </Typography>

    {/* Right: Branch Dropdown (only visible to master users) */}
    {isMaster && (
      <Box sx={{ position: 'relative' }}>
        <FormControl size="small" sx={{ width: 220 }}>
          <InputLabel
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.75rem',
              fontWeight: 500,
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
              top: '-2px',
              '&.Mui-focused': { color: '#64B5F6' },
              '&.MuiFormLabel-filled': { color: 'rgba(255,255,255,0.7)' },
            }}
          >
            Branch
          </InputLabel>
          <Select
            value={selectedBranchId || ''}
            onChange={(e) => setSelectedBranchId(e.target.value)}
            label="Branch"
            displayEmpty
  renderValue={(selected) => {
    if (!selected) {
      return <em>Select Branch</em>;   // fallback when nothing selected
    }
    const branch = branches.find(b => b.branchId === selected);
    return branch ? branch.branchName : selected;
  }}
            startAdornment={
              <InputAdornment position="start">
                <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', ml: 0.5 }} />
              </InputAdornment>
            }
            sx={{
              color: '#ffffff',
              fontWeight: 500,
              fontSize: '0.875rem',
              letterSpacing: '0.3px',
              backgroundColor: 'rgba(255,255,255,0.07)',
              backdropFilter: 'blur(8px)',
              borderRadius: '8px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.15)',
                borderWidth: '1px',
              },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.11)',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                boxShadow: '0 0 0 3px rgba(100,181,246,0.18)',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#64B5F6', borderWidth: '1.5px' },
              },
              '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' },
              '&.Mui-focused .MuiSelect-icon': { color: '#64B5F6' },
              '& .MuiSelect-select': { py: 1.1 },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 0.5,
                  borderRadius: '10px',
                  border: '1px solid rgba(11,31,61,0.12)',
                  boxShadow: '0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)',
                  '& .MuiList-root': { py: 0.5 },
                },
              },
            }}
          >
            {branches.map((branch) => (
              <MenuItem
                key={branch.branchId}
                value={branch.branchId}
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#0B1F3D',
                  px: 2,
                  py: 1.1,
                  mx: 0.5,
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: 'rgba(11,31,61,0.07)' },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25,118,210,0.1)',
                    color: '#1565C0',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: 'rgba(25,118,210,0.15)' },
                  },
                }}
              >
                {branch.branchName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    )}
  </Toolbar>
</AppBar>

      <Paper
        elevation={3}
        sx={{
          padding: 3,
          width: '100%',
          margin: 'auto',
          borderRadius: '0 0 8px 8px',
          backgroundColor: '#fafafa'
        }}
      >
        <LoadingBar progress={loadingBarProgress} color="rgb(152, 106, 182)" height={5} />

        {/* Search and Add Topic Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            gap: 2,
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search topics..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: { xs: '100%', sm: '350px' },
              backgroundColor: '#ffffff',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#0B1F3D',
                },
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={!canAdd ? 'You are not authorized to add' : ''} arrow>
            <span>
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={!canAdd}
                color='primary'
                sx={{
                  width: { xs: '100%', sm: '140px' },
                  height: '56px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  letterSpacing: '0.5px',
                }}
              >
                ADD TOPIC
              </Button>
            </span>
          </Tooltip>
        </Box>
        {/* Dropdowns for Teacher, Class, Section, Subject */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2.5,
            mb: 3,
            p: 2,
            backgroundColor: '#ffffff',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="teacher">Teacher</InputLabel>
            <Select
              id="teacher"
              value={teacherId}
              label="Teacher"
              onChange={(e) => setTeacherId(e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            >
              {teacherData.map((teacher) => (
                <MenuItem key={teacher.employeeId} value={teacher.employeeId}>
                  {teacher.employeeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="class">Class</InputLabel>
            <Select
              id="class"
              value={classId}
              label="Class"
              onChange={(e) => setClassId(e.target.value)}
              disabled={!teacherId}
              sx={{ backgroundColor: '#fff' }}
            >
              {classData.map((classItem) => (
                <MenuItem key={classItem.classId} value={classItem.classId}>
                  {classItem.className}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="section">Section</InputLabel>
            <Select
              id="section"
              value={sectionId}
              label="Section"
              onChange={(e) => setSectionId(e.target.value)}
              disabled={!classId}
              sx={{ backgroundColor: '#fff' }}
            >
              {sectionData.map((sectionItem) => (
                <MenuItem key={sectionItem.sectionId} value={sectionItem.sectionId}>
                  {sectionItem.sectionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="subject">Subject</InputLabel>
            <Select
              id="subject"
              value={subjectId}
              label="Subject"
              onChange={(e) => setSubjectId(e.target.value)}
              disabled={!sectionId}
              sx={{ backgroundColor: '#fff' }}
            >
              {subjectData.map((subjectItem) => (
                <MenuItem key={subjectItem.subjectId} value={subjectItem.subjectId}>
                  {subjectItem.subjectName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>



        {/* Topics Table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}
        >
          <Table
            sx={{
              minWidth: 650,
              '& .MuiTableCell-root': {
                borderRight: '1px solid #e0e0e0',
                fontSize: '0.95rem',
                padding: '14px 16px',
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                fontWeight: 700,
                backgroundColor: '#f8f9fa',
                color: '#2c3e50',
                borderBottom: '2px solid #dee2e6',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
              },
              '& .MuiTableBody-root .MuiTableRow-root': {
                '&:hover': {
                  backgroundColor: '#f5f7fa',
                  transition: 'background-color 0.2s ease',
                },
                '&:last-child td': {
                  borderBottom: 'none',
                }
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Topic Order</TableCell>
                <TableCell>Topic Name</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Covered By</TableCell>
                <TableCell>Date Covered</TableCell>
                <TableCell sx={{ textAlign: 'center', borderRight: 'none !important' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingBarProgress > 0 && loadingBarProgress < 100 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, borderRight: 'none !important' }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
                      Loading topics...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((topic, index) => (
                  <TableRow
                    key={topic.topicId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafbfc',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {topic.topicOrder}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                        {topic.topicName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#495057' }}>
                        {topic.subjectName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#495057' }}>
                        {topic.className}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {topic.status === 'P' ? (
                        <Chip
                          label="Pending"
                          size="small"
                          sx={{
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px'
                          }}
                        />
                      ) : topic.status === 'C' ? (
                        <Chip
                          label="Completed"
                          size="small"
                          sx={{
                            backgroundColor: '#d4edda',
                            color: '#155724',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px'
                          }}
                        />
                      ) : (
                        <Typography variant="body2">{topic.status}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#495057' }}>
                        {topic.coveredByTeacherName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#495057' }}>
                        {topic.dateCovered || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', borderRight: 'none !important' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title={canEdit ? '' : 'You are not authorized to edit'} arrow>
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              color='warning'
                              onClick={() => handleEdit(topic.topicId)}
                              disabled={!canEdit}
                              startIcon={<EditIcon sx={{ fontSize: '1rem' }} />}
                              sx={{
                                minWidth: '85px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                padding: '6px 12px',
                              }}
                            >
                              Edit
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={shouldDisableDelete(topic.status) ? 'Cannot delete completed topics' : (!canDelete ? 'You are not authorized to delete' : '')}
                          arrow
                        >
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              color='error'
                              onClick={() => handleShow(topic.topicId, topic.topicName, topic.status)}
                              disabled={!canDelete || shouldDisableDelete(topic.status)}
                              startIcon={<DeleteIcon sx={{ fontSize: '1rem' }} />}
                              sx={{
                                minWidth: '95px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                padding: '6px 12px',
                              }}
                            >
                              Delete
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, borderRight: 'none !important' }}>
                    <Typography variant="body1" sx={{ color: '#999', fontWeight: 500 }}>
                      No Topics Found
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#bbb', mt: 1 }}>
                      Please select filters to view topics
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Delete Confirmation Modal */}
        <Modal open={showModal} onClose={handleClose}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 450 },
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#0B1F40',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}
              >
                Delete Confirmation
              </Typography>
              <Typography sx={{ color: '#ffd700', fontSize: '1.3rem' }}>⚠️</Typography>
            </Box>
            <Box sx={{ padding: '24px 20px' }}>
              <Typography
                sx={{
                  mb: 3,
                  fontSize: '1rem',
                  color: '#495057',
                  lineHeight: 1.6
                }}
              >
                Are you sure you want to delete{' '}
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: '#0B1F3D'
                  }}
                >
                  {selectedTopic.topicName}
                </Typography>
                ?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#dc3545',
                  mb: 3,
                  fontSize: '0.9rem'
                }}
              >
                This action cannot be undone.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  sx={{
                    borderColor: '#6c757d',
                    color: '#6c757d',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 3,
                    '&:hover': {
                      borderColor: '#5a6268',
                      backgroundColor: '#f8f9fa',
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleDelete}
                  sx={{
                    backgroundColor: '#dc3545',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 3,
                    '&:hover': {
                      backgroundColor: '#c82333',
                    }
                  }}
                >
                  Yes, Delete
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Pagination */}
        {filteredData.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 3,
              p: 2,
              backgroundColor: '#ffffff',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: 2
            }}
          >
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{
                width: { xs: '100%', sm: 150 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              SelectProps={{ native: true }}
            >
              {[5, 10, 25, 50].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: '#495057'
                }}
              >
                Page {page} of {Math.ceil(filteredData.length / rowsPerPage)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => handleChangePage(null, page - 1)}
                  disabled={page === 1}
                  sx={{
                    minWidth: '80px',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    }
                  }}
                >
                  Prev
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleChangePage(null, page + 1)}
                  disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
                  sx={{
                    minWidth: '80px',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    }
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            borderRadius: 2,
            fontWeight: 500
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AllTopics;